import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * <facets-sticky-controls> — banda fija con la barra de controles de la PLP.
 *
 * En modo "filtros como boton" la barra y la grilla de productos comparten la
 * fila 1 del grid de .collection-wrapper: la banda queda sticky con
 * align-self: start y la grilla se corre hacia abajo con un margin-top igual al
 * alto de la banda.
 *
 * Hace falta porque el containing block de un grid item es su grid area: si la
 * barra estuviera sola en su fila, su area mediria exactamente lo que mide ella
 * y position: sticky no tendria rango donde desplazarse.
 *
 * El alto solo se conoce en runtime (depende del contenido, la tipografia y el
 * ancho), asi que se publica como --flt-controls-height en el .collection-wrapper
 * mas cercano. Mismo patron que header.js con --header-height.
 *
 * Ademas marca data-stuck mientras la banda esta efectivamente pegada arriba,
 * para que el CSS pueda sacarle la separacion inferior al separador.
 *
 * Tambien vigila con un MutationObserver el style inline del host: un morph
 * disparado por un filtro puede pisarlo (ver el comentario en
 * connectedCallback) y perder --flt-controls-height sin que el
 * ResizeObserver se entere. Esto ya no es parte del port byte-a-byte que
 * Task 1 verifico por checksum -- es un fix a un bug real del tema de
 * origen, confirmado ahi tambien.
 */

class FacetsStickyControls extends HTMLElement {
  /** @type {HTMLElement | null} */
  #host = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #styleObserver = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #scrollRafId = null;

  // 'screen and' para ser congruente con las @media del CSS que consume data-stuck
  // y --flt-controls-height: sin eso, en print el JS se creeria en desktop.
  #desktopQuery = window.matchMedia('screen and (min-width: 750px)');

  #stuck = false;

  /**
   * Margen inferior que el CSS le colapsa al separador mientras la banda esta
   * pegada. Se le vuelve a sumar al alto medido para publicar siempre el alto
   * natural. Ver #publishHeight.
   */
  #stuckMarginCompensation = 0;

  connectedCallback() {
    this.#host = /** @type {HTMLElement | null} */ (this.closest('.collection-wrapper'));
    if (!this.#host) return;

    // Idempotente ante morphs de la Section Rendering API, igual que overlay-scroll.js.
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;

      const box = entry.borderBoxSize[0];
      if (!box) return;

      // Redondeamos igual que header.js para no disparar reflows por fracciones.
      this.#publishHeight(Math.round(box.blockSize));
    });

    this.#resizeObserver.observe(this);

    // Un morph disparado por un filtro puede pisarle el style inline al host:
    // assets/morph.js solo preserva el style corrido en
    // FLOATING-PANEL-COMPONENT y FIELDSET, no en .collection-wrapper, y
    // data-skip-node-update solo protege los atributos de esta banda, no los
    // del host. Si el alto de la banda no cambio con el filtro, el
    // ResizeObserver de arriba no vuelve a disparar y nadie repone
    // --flt-controls-height. Este observer mira puntualmente el style del
    // host y, si ve que la propiedad desaparecio, la vuelve a publicar.
    // Idempotente ante morphs, igual que el ResizeObserver de arriba.
    this.#styleObserver?.disconnect();
    this.#styleObserver = new MutationObserver(() => this.#handleHostStyleChange());
    this.#styleObserver.observe(this.#host, { attributes: true, attributeFilter: ['style'] });

    this.#syncScrollBinding();
    scrollContainerMediaQuery.addEventListener('change', this.#syncScrollBinding);
    this.#desktopQuery.addEventListener('change', this.#syncScrollBinding);
    window.addEventListener('resize', this.#handleScroll, { passive: true });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.#styleObserver?.disconnect();
    this.#styleObserver = null;

    this.#unbindScroll();
    scrollContainerMediaQuery.removeEventListener('change', this.#syncScrollBinding);
    this.#desktopQuery.removeEventListener('change', this.#syncScrollBinding);
    window.removeEventListener('resize', this.#handleScroll);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    this.#host?.style.removeProperty('--flt-controls-height');
    // Viaja siempre junto con --flt-controls-height -- nunca deben existir por
    // separado. Ver el comentario en #publishHeight.
    this.#host?.style.removeProperty('--flt-controls-row');
    // El marcador y la custom property se crean y se destruyen juntos: si quedara
    // el marcador sin la medida, la grilla volveria a la fila 1 con margin-top 0.
    delete this.dataset.measured;
    delete this.dataset.stuck;
    this.#stuck = false;
    this.#stuckMarginCompensation = 0;
    this.#host = null;
  }

  /**
   * Publica el alto NATURAL de la banda, el que tendria sin estar pegada.
   *
   * Al pegarse, el CSS le saca el margin-bottom al separador y la banda se achica.
   * Publicar ese alto menor correria la grilla hacia arriba y la pagina saltaria
   * justo al fijarse, asi que se le vuelve a sumar el margen colapsado. Compensar
   * en vez de congelar mantiene el valor correcto si el viewport cambia mientras
   * la banda esta pegada.
   *
   * @param {number} height - Alto medido, ya redondeado.
   */
  #publishHeight(height) {
    if (!height) return;

    const naturalHeight = height + this.#stuckMarginCompensation;
    this.#host?.style.setProperty('--flt-controls-height', `${naturalHeight}px`);
    // Viaja junto con el alto, nunca por separado: el CSS lee esta variable en
    // vez de un grid-row: 1 literal para que, si algo vuelve a perder
    // --flt-controls-height antes de que #handleHostStyleChange llegue a
    // reponerla, la grilla caiga a grid-row: auto (debajo de la banda) en vez
    // de superponerse con margin-top: 0.
    this.#host?.style.setProperty('--flt-controls-row', '1');

    // Recien con la medida publicada el CSS puede mandar la grilla a la fila 1.
    // Antes de esto la grilla se apila debajo de la banda: sin sticky, pero sin
    // productos tapados si el script tarda o no llega a cargar.
    this.dataset.measured = '';
  }

  /**
   * Reacciona a que el host haya perdido --flt-controls-height (ver el
   * comentario en connectedCallback). Si la propiedad sigue presente no hace
   * nada -- esa guarda es la que corta el loop, porque #publishHeight tambien
   * escribe el style del host y dispararia este mismo observer con su propia
   * escritura.
   *
   * Vuelve a medir en vez de reusar el ultimo alto publicado: el contenido de
   * la banda puede cambiar legitimamente con el filtro (la cuenta de
   * productos, la burbuja), asi que un valor cacheado quedaria desactualizado.
   */
  #handleHostStyleChange() {
    if (!this.#host) return;
    if (this.#host.style.getPropertyValue('--flt-controls-height')) return;

    this.#publishHeight(Math.round(this.getBoundingClientRect().height));
  }

  #unbindScroll() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = null;
  }

  /**
   * Engancha el listener de scroll al contenedor correcto, o lo desengancha del
   * todo bajo 750px, donde la banda no es sticky y el trabajo no sirve para nada.
   *
   * Se reevalua en dos cruces de breakpoint: 750px (la banda deja de ser sticky) y
   * 990px (el contenedor de scroll pasa de .page-wrapper al documento, igual que en
   * header.js). Desengancha siempre primero, asi queda idempotente.
   */
  #syncScrollBinding = () => {
    this.#unbindScroll();

    if (!this.#desktopQuery.matches) {
      this.#setStuck(false);
      return;
    }

    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#syncStuck();
  };

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#syncStuck();
    });
  };

  /**
   * Sin pegar, el borde superior de la banda coincide con el del wrapper: es
   * align-self: start en la fila 1. Al pegarse, la banda se queda arriba mientras
   * el wrapper sigue subiendo, asi que alcanza con comparar los dos rects.
   *
   * Se comparan rects en vez de mirar el top resuelto porque ese top es dinamico:
   * vale 0 o --header-height - 1px segun el estado del header.
   */
  #syncStuck() {
    if (!this.#host || !this.#desktopQuery.matches) return;

    this.#setStuck(
      Math.round(this.getBoundingClientRect().top) > Math.round(this.#host.getBoundingClientRect().top)
    );
  }

  /**
   * Antes de marcar data-stuck se lee el margin-bottom que el CSS esta por
   * colapsar, para poder sumarselo de vuelta al alto medido. Se lee aca y no en
   * #publishHeight porque despues de marcar el atributo ya vale 0.
   *
   * @param {boolean} stuck
   */
  #setStuck(stuck) {
    if (stuck === this.#stuck) return;

    if (stuck) {
      const separator = this.querySelector('.facets-controls-separator');
      this.#stuckMarginCompensation = separator
        ? parseFloat(getComputedStyle(separator).marginBottom) || 0
        : 0;
      this.#stuck = true;
      this.dataset.stuck = '';
      return;
    }

    this.#stuck = false;
    this.#stuckMarginCompensation = 0;
    delete this.dataset.stuck;
  }
}

if (!customElements.get('facets-sticky-controls')) {
  customElements.define('facets-sticky-controls', FacetsStickyControls);
}
